import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { patientEnvironmentToxinsApi } from "apis";
import {
  ENVIRONMENT_TOXINS_CONDITIONS,
  ENVIRONMENT_TOXINS_TEXT_FIELDS,
  type EnvironmentToxinsConditionKey,
  type EnvironmentToxinsTextFieldKey,
} from "types/environment-toxins";

import IntakeCheckboxField from "./IntakeCheckboxField";
import IntakeField from "./IntakeField";
import IntakeHeader from "./IntakeHeader";
import LayoutWrapper from "./LayoutWrapper";

interface EnvironmentToxinsProps {
  patientId: string;
}

const EnvironmentToxins = ({ patientId }: EnvironmentToxinsProps) => {
  const { data, isError, error } = useQuery({
    queryKey: ["environmentToxins", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      try {
        return await patientEnvironmentToxinsApi.getEnvironmentToxinsByPatientId(
          {
            patientId: patientId,
          }
        );
      } catch (error: unknown) {
        if (
          (error as { status?: number })?.status === 400 ||
          (error as { status?: number })?.status === 404
        ) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!patientId,
  });

  const renderCheckboxField = (condition: {
    key: EnvironmentToxinsConditionKey;
    label: string;
  }) => {
    const value = data?.[condition.key] || false;
    return (
      <IntakeCheckboxField
        key={condition.key}
        label={condition.label}
        value={value}
        disabled={true}
      />
    );
  };

  const renderTextField = (field: {
    key: EnvironmentToxinsTextFieldKey;
    label: string;
  }) => {
    const value = data?.[field.key];
    let formattedValue = "";
    if (field.key === "chemicalExposure") {
      formattedValue = (value as string[])?.join(", ") || "";
    } else {
      formattedValue = value as string;
    }
    return (
      <IntakeField
        key={field.key}
        label={field.label}
        value={formattedValue}
        disabled={true}
        rows={field.key === "additionalInformation" ? 3 : 1}
      />
    );
  };

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Error loading environment toxins: {(error as Error).message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        "&::-webkit-scrollbar": { display: "none" },
        "-ms-overflow-style": "none",
        "scrollbar-width": "none",
        "& *": {
          "&::-webkit-scrollbar": { display: "none" },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        },
      }}
    >
      <IntakeHeader title="Environment Toxins" />
      <LayoutWrapper itemsPerRow={2} spacing={3}>
        {ENVIRONMENT_TOXINS_CONDITIONS.map(renderCheckboxField)}
      </LayoutWrapper>
      <LayoutWrapper itemsPerRow={1} spacing={3}>
        {ENVIRONMENT_TOXINS_TEXT_FIELDS.map((field) => {
          const elements = [renderTextField(field)];
          // If home field is "Other", show homeOther field right after
          if (
            field.key === "home" &&
            data?.home === "Other" &&
            data?.homeOther
          ) {
            elements.push(
              <IntakeField
                key="homeOther"
                label="Home Environment (Other Details)"
                value={data.homeOther}
                disabled={true}
                rows={1}
              />
            );
          }
          return elements;
        })}
      </LayoutWrapper>
    </Box>
  );
};

export default EnvironmentToxins;
